import React, { useEffect } from "react";
import HomeChart from "./HomeChart";
import SecondHome from "./SecondHome";
import ThirdHome from "./ThirdHome";
import FourthHome from "./FourthHome";
import Footer from "./Footer";
import "../assets/scss/module/home.scss";

export default function Home({ name = "Guest", theme = "green" }) {
    const primary = theme === "green" ? "#16a34a" : "#0f62fe";
    const accent = theme === "green" ? "#0ea56a" : "#0b84ff";

    useEffect(() => {
        document.title = `${name} — Welcome`;
    }, [name]);

    const goto = (path) => {
        if (typeof window !== "undefined") window.location.href = path;
    };

    const canvasRef = React.useRef(null);
    const [price, setPrice] = React.useState(null);
    const wsRef = React.useRef(null);
    const dataRef = React.useRef([]);

    // Draw helper
    const drawChart = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        ctx.scale(dpr, dpr);

        // clear
        ctx.clearRect(0, 0, w, h);
        // use the configured canvas background from styles.chart if available,
        // falling back to the previous light color
        const canvasBg = (typeof styles !== "undefined" && styles.chart && styles.chart.background) ? styles.chart.background : "#f6faf7";
        ctx.fillStyle = canvasBg;
        ctx.fillRect(0, 0, w, h);

        const points = dataRef.current;
        if (!points || points.length === 0) return;

        // padding
        const pad = 8;
        const innerW = w - pad * 2;
        const innerH = h - pad * 2;

        // compute min/max
        let min = Infinity, max = -Infinity;
        for (let p of points) {
            if (p.v < min) min = p.v;
            if (p.v > max) max = p.v;
        }
        if (min === max) { min = min * 0.999; max = max * 1.001; }

        // map
        const stepX = innerW / Math.max(1, points.length - 1);
        ctx.lineJoin = "round";
        ctx.lineCap = "round";

        // draw grid lines (optional, controlled by styles.chart.hideGrid or styles.chart.gridColor)
        const hideGrid = (typeof styles !== "undefined" && styles.chart && styles.chart.hideGrid) === true;
        const gridColor = (typeof styles !== "undefined" && styles.chart && styles.chart.gridColor) ? styles.chart.gridColor : "#e9f2ee";
        if (!hideGrid) {
            ctx.strokeStyle = gridColor;
            ctx.lineWidth = 1;
            ctx.beginPath();
            for (let i = 0; i <= 2; i++) {
                const y = pad + (innerH / 2) * i;
                ctx.moveTo(pad, y);
                ctx.lineTo(pad + innerW, y);
            }
            ctx.stroke();
        }

        // draw area/line
        ctx.beginPath();
        for (let i = 0; i < points.length; i++) {
            const x = pad + i * stepX;
            const t = (points[i].v - min) / (max - min);
            const y = pad + innerH - t * innerH;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        // shadowed stroke for primary color
        ctx.strokeStyle = primary;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // draw faint area under the line
        ctx.lineTo(pad + innerW, pad + innerH);
        ctx.lineTo(pad, pad + innerH);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, pad, 0, pad + innerH);
        grad.addColorStop(0, `${primary}33`);
        grad.addColorStop(1, `${primary}00`);
        ctx.fillStyle = grad;
        ctx.fill();

        // draw last price marker
        const last = points[points.length - 1];
        const lastX = pad + (points.length - 1) * stepX;
        const lastT = (last.v - min) / (max - min);
        const lastY = pad + innerH - lastT * innerH;
        ctx.beginPath();
        ctx.fillStyle = "#fff";
        ctx.strokeStyle = primary;
        ctx.lineWidth = 1.5;
        ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // price label
        const label = `${last.v.toFixed(2)} USD`;
        ctx.font = "12px Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial";
        ctx.fillStyle = "#0b1220";
        ctx.textBaseline = "middle";
        const labelW = ctx.measureText(label).width + 12;
        const rectX = Math.max(6, Math.min(w - labelW - 6, lastX - labelW / 2));
        const rectY = lastY - 12;
        ctx.fillStyle = "#fff";
        roundRect(ctx, rectX, rectY, labelW, 24, 6);
        ctx.fillStyle = primary;
        ctx.fillText(label, rectX + 6, rectY + 12);

        // helper for rounded rect
        function roundRect(ctx, x, y, width, height, radius) {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.arcTo(x + width, y, x + width, y + height, radius);
            ctx.arcTo(x + width, y + height, x, y + height, radius);
            ctx.arcTo(x, y + height, x, y, radius);
            ctx.arcTo(x, y, x + width, y, radius);
            ctx.closePath();
            ctx.fill();
        }
    };

    // fetch initial historical data + open websocket for live updates
    React.useEffect(() => {
        let mounted = true;

        // Fetch last 60 1m candles from Binance (BTCUSDT)
        fetch("https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=60")
            .then((r) => r.json())
            .then((klines) => {
                if (!mounted) return;
                const points = klines.map((k) => ({ t: k[0], v: parseFloat(k[4]) }));
                dataRef.current = points;
                if (points.length) setPrice(points[points.length - 1].v);
                drawChart();
            })
            .catch(() => {
                // ignore fetch errors silently
            });

        // open kline websocket for BTCUSDT 1m to get live candle updates
        try {
            const ws = new WebSocket("wss://stream.binance.com:9443/ws/btcusdt@kline_1m");
            wsRef.current = ws;
            ws.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    if (msg && msg.k) {
                        const close = parseFloat(msg.k.c);
                        const time = msg.k.t;
                        const last = dataRef.current[dataRef.current.length - 1];
                        if (last && last.t === time) {
                            last.v = close;
                        } else {
                            dataRef.current.push({ t: time, v: close });
                            if (dataRef.current.length > 120) dataRef.current.shift();
                        }
                        setPrice(close);
                        drawChart();
                    }
                } catch (_) {}
            };
            ws.onerror = () => {};
        } catch (_) {}

        // redraw on window resize
        const onResize = () => drawChart();
        window.addEventListener("resize", onResize);

        return () => {
            mounted = false;
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (_) {}
            }
            window.removeEventListener("resize", onResize);
        };
    }, []); // run once
    const [showSecondHome, setShowSecondHome] = React.useState(() => {
        if (typeof window === "undefined") return true;
        return window.innerWidth > 1000;
    });

    React.useEffect(() => {
        const onResize = () => setShowSecondHome(window.innerWidth > 1000);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return (
        <div>
        <main style={{ ...styles.container }}>
            <div style={styles.hero}>
                <div style={styles.heroLeft}>
                    <div style={{ ...styles.badge, background: primary }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M4 16L10 10L14 14L20 8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ marginLeft: 8, fontWeight: 700 }}>Welcome</span>
                    </div>

                    <h1 style={{ ...styles.h1, color: "#0b1220" }}>
                        Hello{ name && name !== "Guest" ? `, ${name}` : "" } — discover a clean Trading Experience
                    </h1>
                    <style>{`main h1 { color: #f1f5f9 !important; }`}</style>

                    <p style={styles.lead}>
                        Welcome to our modern trading platform, built for speed, clarity, and control. Access markets effortlessly, manage your portfolio confidently, and trade smarter with intuitive, reliable tools.
                    </p>

                    <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                        <button
                            onClick={() => goto("/exchange")}
                            style={{ ...styles.cta, background: primary, boxShadow: `0 8px 28px ${primary}22`, borderColor: primary }}
                        >
                            Explore Charts
                        </button>
                        <button
                            onClick={() => goto("/profile")}
                            style={{ ...styles.secondary, borderColor: primary, color: primary }}
                        >
                            View Profile
                        </button>
                    </div>

                    <div style={styles.features}>
                        <div style={styles.featureItem}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M3 12h18" stroke={primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6 8v8" stroke={primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 5v14" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18 10v6" stroke={primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <div style={{ fontWeight: 700, color: "#94a3b8" }}>Deep analytics</div>
                                <div style={styles.featureText}>Crisp charts and quick overviews.</div>
                            </div>
                        </div>

                        <div style={styles.featureItem}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M12 3l7 4v6c0 5-4 8-7 8s-7-3-7-8V7l7-4z" stroke={primary} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <div style={{ fontWeight: 700, color: "#94a3b8" }}>Enterprise security</div>
                                <div style={styles.featureText}>Privacy-focused and simple controls.</div>
                            </div>
                        </div>
                        <span
                            style={{ display: "none" }}
                            ref={(el) => {
                                if (!el) return;
                                const h1 = document.querySelector("main h1");
                                if (!h1 || h1.dataset._greenified) return;
                                try {
                                    const html = h1.innerHTML;
                                    const replaced = html.replace(/(Trading\s+Experience)(?![\s\S]*Trading\s+Experience)/i, `<span style="color: ${primary}">$1</span>`);
                                    if (replaced !== html) {
                                        h1.innerHTML = replaced;
                                        h1.dataset._greenified = "1";
                                    }
                                } catch (_) {}
                            }}
                        />
                        <div style={styles.featureItem}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <circle cx="12" cy="8" r="2" stroke={primary} strokeWidth="1.4" />
                                <path d="M6 20c1.5-3 4.5-4 6-4s4.5 1 6 4" stroke={accent} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <div style={{ fontWeight: 700, color: "#94a3b8" }}>Custom workflows</div>
                                <div style={styles.featureText}>Tailor the experience to your workflow.</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.heroRight}>
                    {/* orbit container centers the circular path */}
                    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {/* rotating wrapper creates the orbit path */}
                        <div style={{ display: "inline-block", animation: "orbit 12s linear infinite" }}>
                            {/* image is offset outward and counter-rotates so it stays upright while moving in a circle */}
                            <img
                                style={{
                                    ...styles.homeImg,
                                    display: "block",
                                    transform: "translateX(44px) scale(1.35)",
                                    animation: "counterOrbit 12s linear infinite",
                                }}
                                src={require("../assets/img/btc1.webp")}
                                className="homeImg1"
                            />
                        </div>
                    </div>

                    <style>{`
                        @keyframes orbit {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                        @keyframes counterOrbit {
                            from { transform: translateX(20px) rotate(0deg) scale(1.4); }
                            to { transform: translateX(20px) rotate(-360deg) scale(1.4); }
                        }
                    `}</style>
                </div>
            </div>
        </main>
        {showSecondHome && <SecondHome/>}
        <FourthHome/>
        <ThirdHome/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <Footer/>
        </div>
    );
}

const styles = {
    container: {
        fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
        padding: 30,
        maxWidth: 1100,
        margin: "0 auto",
        boxSizing: "border-box",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    hero: {
        display: "grid",
        gridTemplateColumns: "1fr 380px",
        gap: 28,
        width: "100%",
        alignItems: "center",
        marginBottom: 60,
    },
    heroLeft: {
        maxWidth: 700,
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "8px 12px",
        borderRadius: 999,
        color: "#fff",
        fontSize: 13,
        width: "fit-content",
    },
    h1: {
        margin: "18px 0 0",
        fontSize: 34,
        lineHeight: 1.05,
    },
    lead: {
        marginTop: 12,
        color: "#606b73",
        fontSize: 16,
        maxWidth: 560,
    },
    cta: {
        padding: "12px 18px",
        borderRadius: 10,
        color: "#fff",
        fontWeight: 700,
        border: "1px solid",
        cursor: "pointer",
    },
    secondary: {
        padding: "10px 14px",
        borderRadius: 10,
        background: "transparent",
        fontWeight: 700,
        border: "1px solid",
        cursor: "pointer",
    },
    features: {
        display: "flex",
        gap: 18,
        marginTop: 22,
    },
    featureItem: {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
    },
    featureText: {
        fontSize: 13,
        color: "#6b7780",
        marginTop: 4,
    },
    heroRight: {
        display: "flex",
        justifyContent: "center",
        width: "100%",
    },
    homeImg: {
        transform: "scale(1.45)",
    },
    // Make the mock card taller so the live chart area can be bigger
    mockCard: {
        width: "100%",
        // dark gray background that blends well with the primary accent;
        // using a subtle vertical gradient for depth
        background: "linear-gradient(180deg, #1b4e55ff 0%, #0f2a49ff 100%)",
        borderRadius: 12,
        padding: 16,
        // darker, softer shadow for a muted, modern look on dark backgrounds
        boxShadow: "0 12px 34px rgba(2,6,23,0.6)",
        // very subtle border so the card reads as a panel on dark pages
        border: "1px solid rgba(255, 255, 255, 0.04)",
        // increased minimum height to give the chart more vertical space
        minHeight: 380,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        // default text color inside the dark card
        color: "#e6eef8",
    },

    chart: {
        width: "100%",
        height: 240,
        marginTop: 14,
        borderRadius: 8,
        display: "block",
        background: "transparent",
        hideGrid: true,
        gridColor: "transparent",
        disableArea: true,
    },
    
};
